import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signOut,
  deleteUser
} from "firebase/auth";
import { auth } from '../firebase-config';
import { createUserProfile } from '../Helpers/firebaseHelpers';
import { getFirebaseErrorMessage } from '../Helpers/authErrorMessages';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from "react-icons/fi";

const SignUp = () =>
{
  const [username, setUsername] = useState('');
  const [Standard, setStandard] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const RESERVED_USERNAMES = [
    "admin",
    "administrator",
    "support",
    "help",
    "owner",
    "staff",
    "system",
    "root",
    "eduendurance",
    "official",
    "tanx",
    "tanthetitanx",
    "tanxofficial",
    "tanx_official",
    "tanthetitanx_official",
    "tanthetitanxofficial",
  ];

  const navigate = useNavigate();

  const handleSignUp = async (event) =>
  {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try
    {
      if (!username)
      {
        setError('Username is required.');
        return;
      }

      const trimmedUsername = username.trim();

      if (trimmedUsername.length < 3)
      {
        setError("Username must be at least 3 characters long.");
        return;
      }

      if (trimmedUsername.length > 20)
      {
        setError("Username cannot exceed 20 characters.");
        return;
      }

      const usernameRegex = /^[A-Za-z0-9_]+$/;

      if (!usernameRegex.test(trimmedUsername))
      {
        setError("Username may contain only letters, numbers, and underscores.");
        return;
      }

      if (trimmedUsername.startsWith("_") || trimmedUsername.endsWith("_"))
      {
        setError("Username cannot begin or end with an underscore.");
        return;
      }

      if (trimmedUsername.includes("__"))
      {
        setError("Username cannot contain consecutive underscores.");
        return;
      }

      if (RESERVED_USERNAMES.includes(trimmedUsername.toLowerCase()))
      {
        setError("This username is reserved. Please choose another.");
        return;
      }

      const response = await axios.get(`https://eduendurance-backend.onrender.com/api/users/check-username/${trimmedUsername}`);

      if (!response.data.available)
      {
        setError('This username is already taken.');
        return;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_\-+=])[A-Za-z\d@$!%*?&^#()_\-+=]{8,}$/;
      if (!passwordRegex.test(password))
      {
        setError(
          "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character."
        );
        return;
      }

      const cred = await createUserWithEmailAndPassword(auth, email, password);

      await updateProfile(cred.user, { displayName: trimmedUsername });
      await cred.user.getIdToken(true);

      const profileResult = await createUserProfile({
        Standard: Number(Standard),
        userName: trimmedUsername
      }, cred.user);

      if (!profileResult.success)
      {
          try
          {
              await deleteUser(cred.user);
          }
          catch (cleanupError)
          {
              console.error("Rollback failed:", cleanupError);
          }

          setError(profileResult.error || "Failed to create user profile.");

          return;
      }
      await sendEmailVerification(cred.user);
      await signOut(auth);

      navigate("/verify-email", {
        state: { email }
      });
    }
    catch (err)
    {
      console.error(err);

      if (err.code)
      {
        setError(getFirebaseErrorMessage(err.code));
      }
      else if (err.response?.data?.message)
      {
        setError(err.response.data.message);
      }
      else
      {
        setError("Something went wrong. Please try again later.");
      }

      console.log(err.response?.status, err.response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-white">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg w-full max-w-md">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-800 dark:text-white">
          Sign Up
        </h2>

        {error && (
          <div className="bg-red-500 text-white py-3 px-4 rounded-lg mb-4 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label className="block text-xl font-medium text-gray-700 dark:text-gray-300">
              Username
            </label>
            <input
              type="text"
              maxLength={20}
              minLength={3}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              pattern="^[A-Za-z0-9_]+$"
              title="Only letters, numbers, and underscores are allowed"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              required
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 dark:text-gray-300">
              Standard
            </label>
            <input
              type="number"
              value={Standard}
              onChange={(e) => setStandard(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              min="1"
              max="12"
              required
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 dark:text-gray-300">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              required
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>

            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                className="block w-full px-3 py-2 pr-12 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                required
              />

              <span
                role="button"
                tabIndex={0}
                onClick={() => setShowPassword(!showPassword)}
                onKeyDown={(e) =>
                {
                  if (e.key === "Enter" || e.key === " ")
                    setShowPassword(!showPassword);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer p-1 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </span>
            </div>
          </div>

          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Must contain at least 8 characters, including an uppercase letter,
            lowercase letter, number, and special character.
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-lg font-medium py-2 px-4 rounded-lg shadow-md transition-transform hover:scale-105"
          >
            {loading ? "Creating Account..." : "Sign Up"}
          </button>
        </form>

        <p className="mt-4 text-center text-lg text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/signin')}
            className="text-blue-600 hover:text-blue-500 font-medium"
          >
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignUp;